import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/contexts/AuthContext';
import { fallbackCertifications } from '../fallbackCertifications';
import {
  listCertifications, listCommunityTrainings, listTeacherLearningProgress,
  listLearningModules, listLearningTrainings,
} from '../services/learningCenterService';
import type { LearningProgressEntry } from '../services/learningCenterService';
import { Certification, CommunityTraining, LearningModule, LearningTraining, resolveCertificationPath } from '../types';
import CommunityDiscover from './CommunityDiscover';
import './Resources.css';

const Resources: React.FC = () => {
  const { currentUser } = useAuth();
  const [certifications, setCertifications] = useState<Certification[]>([]);
  const [community, setCommunity] = useState<CommunityTraining[]>([]);
  const [libraryTrainings, setLibraryTrainings] = useState<LearningTraining[]>([]);
  const [learningModules, setLearningModules] = useState<LearningModule[]>([]);
  const [progress, setProgress] = useState<Record<string, LearningProgressEntry>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([
      listCertifications().catch(() => [] as Certification[]),
      listCommunityTrainings().catch(() => [] as CommunityTraining[]),
      listLearningTrainings().catch(() => [] as LearningTraining[]),
      listLearningModules().catch(() => [] as LearningModule[]),
      currentUser?.uid ? listTeacherLearningProgress(currentUser.uid).catch(() => [] as LearningProgressEntry[]) : Promise.resolve([]),
    ]).then(([managed, approved, reusable, reusableModules, savedProgress]) => {
      if (!active) return;
      setCertifications((managed.length ? managed : fallbackCertifications).filter(certification => certification.kind !== 'onboarding'));
      setCommunity(approved);
      setLibraryTrainings(reusable);
      setLearningModules(reusableModules);
      setProgress(Object.fromEntries(savedProgress.map(item => [`${item.certificationId}_${item.stepId}`, item])));
    }).catch(() => active && setCertifications(fallbackCertifications.filter(certification => certification.kind !== 'onboarding')))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [currentUser?.uid]);

  const certificationCounts = (certification: Certification) => {
    const assignedModules = (certification.moduleIds || []).map(id => learningModules.find(item => item.id === id)).filter(Boolean) as LearningModule[];
    const moduleTrainingIds = assignedModules.flatMap(module => module.trainingIds);
    const directTrainings = (certification.trainingIds || []).filter(id => !moduleTrainingIds.includes(id)).map(id => libraryTrainings.find(item => item.id === id)).filter(Boolean) as LearningTraining[];
    const stepComplete = certification.steps.filter(step => progress[`${certification.id}_${step.id}`]?.status === 'completed').length;
    const moduleComplete = assignedModules.filter(module => {
      const available = module.trainingIds.map(id => libraryTrainings.find(item => item.id === id)).filter(Boolean) as LearningTraining[];
      return available.length > 0 && available.every(training => progress[`${certification.id}_training:${training.id}`]?.status === 'completed');
    }).length;
    const directComplete = directTrainings.filter(training => progress[`${certification.id}_training:${training.id}`]?.status === 'completed').length;
    return { complete: stepComplete + moduleComplete + directComplete, total: certification.steps.length + assignedModules.length + directTrainings.length };
  };
  const nextCertification = certifications.find(certification => { const counts = certificationCounts(certification); return counts.complete < counts.total; });
  const nextPathItem = nextCertification ? resolveCertificationPath(nextCertification).find(item => {
    if (item.kind === 'step') return progress[`${nextCertification.id}_${item.id}`]?.status !== 'completed';
    if (item.kind === 'module') {
      const learningModule = learningModules.find(entry => entry.id === item.id);
      if (!learningModule) return false;
      const available = learningModule.trainingIds.map(id => libraryTrainings.find(training => training.id === id)).filter(Boolean) as LearningTraining[];
      return !available.length || available.some(training => progress[`${nextCertification.id}_training:${training.id}`]?.status !== 'completed');
    }
    return progress[`${nextCertification.id}_training:${item.id}`]?.status !== 'completed';
  }) : undefined;
  const nextTitle = nextPathItem?.kind === 'step'
    ? nextCertification?.steps.find(step => step.id === nextPathItem.id)?.title
    : nextPathItem?.kind === 'module'
      ? learningModules.find(entry => entry.id === nextPathItem.id)?.title
      : libraryTrainings.find(entry => entry.id === nextPathItem?.id)?.title;
  const nextHash = nextPathItem?.kind === 'step' ? `#learning-${nextPathItem.id}` : nextPathItem?.kind === 'module' ? `#module-${nextPathItem.id}` : '';
  const communityLibrary = libraryTrainings.filter(training => training.communityEducation);

  return <main className="learning-center">
    <header className="learning-center__header"><span className="learning-center__eyebrow">Teacher development</span><h1>Your path at Overture</h1><p>See what each stage unlocks, then pick up exactly where you left off.</p></header>

    {!loading && nextCertification && <Link className="learning-center__continue" to={`/app/resources/${nextCertification.id}${nextHash}`}>
      <span className="learning-center__continue-icon">→</span><span><small>Continue where you left off</small><strong>{nextTitle || nextCertification.title}</strong><em>{nextCertification.title}</em></span><b>Continue →</b>
    </Link>}

    <section className="learning-stage" aria-labelledby="cert-stage"><div className="learning-stage__heading"><div><small>Build your teaching practice</small><h2 id="cert-stage">Certifications</h2></div></div>
      {loading ? <p>Loading certification paths…</p> : <div className="learning-center__certifications">{certifications.map(certification => {
        const { complete, total } = certificationCounts(certification);
        const pending = certification.steps.filter(step => progress[`${certification.id}_${step.id}`]?.status === 'pending_review').length;
        const percent = total ? Math.round((complete / total) * 100) : 0;
        return <Link className="path-card path-card--certification" to={`/app/resources/${certification.id}`} key={certification.id}>
          <div className="path-card__top"><div className="path-card__progress" style={{ '--progress': `${percent * 3.6}deg` } as React.CSSProperties}><span>{complete}/{total}</span></div><div className="path-card__copy"><span className="learning-center__section-label">Certification path</span><h3>{certification.title}</h3><p>{certification.description}</p>{pending > 0 && <small className="path-card__pending">● {pending} step{pending === 1 ? '' : 's'} awaiting review</small>}</div><span className="path-card__arrow">→</span></div>
        </Link>;
      })}</div>}
    </section>

    <CommunityDiscover libraryTrainings={communityLibrary} communityTrainings={community} />
  </main>;
};

export default Resources;
